import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ensureFreshSpaceGuestToken,
  fetchSkpEnvironment,
  resolvePublishedSpace,
} from "../../api";
import {
  DesktopSpaceBrowser,
  readPersistedSpaceView,
} from "../../components/space/desktopSpaceBrowser";
import { DesktopSpaceSkeleton } from "../../components/space/desktopSpaceSkeleton";
import { PublishedSpaceResolution, SkpEnvironment } from "../../entities";
import {
  DRIVE_FAVICON_HREF,
  DRIVE_PRODUCT_NAME,
  usePageChrome,
} from "../../hooks/usePageChrome";
import { ConditionalEnvironmentProvider } from "../../providers/environment";
import TransferPageSkeleton from "../skeletons/transfer";

type PublishedSpacePageParams = {
  spaceUid: string;
};

/**
 * Skeleton→content transition, run strictly sequentially so the skeleton and the
 * real content are *never* visible at the same time: the skeleton fades out
 * first, and only once it's gone does the content fade in.
 */
const SKELETON_FADE_MS = 240;
const CONTENT_FADE_MS = 300;

/**
 * Public (unauthenticated) viewer for a *published space*.
 *
 * A published space is a directory tree, so on top of the share-link viewer's
 * file listing it adds folder navigation: breadcrumbs, folder cards and a
 * grid/list layout. A single {@link DesktopSpaceBrowser} serves every width —
 * it compacts responsively down to phones — wrapped in the shared top banner +
 * footer, with a skeleton→content crossfade on load.
 *
 * @see {@link https://dev.skalio.net/drive/usecases/sharing/#publish_an_entire_space}
 */
export default function PublishedSpacePage() {
  const { spaceUid } = useParams<PublishedSpacePageParams>();
  if (!spaceUid) throw new Error("spaceUid is undefined");

  // Environment powers the shared top banner + footer. Fetched best-effort
  // inside the space query (like the share-link viewer) so the space still
  // renders if it fails; kept in a ref so a re-render doesn't refetch it.
  const environmentRef = useRef<SkpEnvironment | undefined>(undefined);

  const { status, data, error } = useQuery<
    PublishedSpaceResolution,
    AxiosError
  >({
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: ["publishedSpace", spaceUid],
    queryFn: async () => {
      try {
        environmentRef.current = await fetchSkpEnvironment();
      } catch {
        // Best-effort; the space must still render without the environment.
      }
      return resolvePublishedSpace(spaceUid);
    },
  });

  // Drive tab chrome; the title names the space once it has resolved.
  usePageChrome({
    title: data?.spaceName
      ? `${data.spaceName} - ${DRIVE_PRODUCT_NAME}`
      : DRIVE_PRODUCT_NAME,
    faviconHref: DRIVE_FAVICON_HREF,
  });

  // Reactive guest-cookie refresh (matching the share-link viewer's rationale in
  // ShareLink.tsx: no exp-timer, refresh on visibility + before un-retryable
  // actions). Keeps long-idle tabs able to download/preview.
  useEffect(() => {
    if (status !== "success") return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        void ensureFreshSpaceGuestToken(spaceUid);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [status, spaceUid]);

  // The skeleton mirrors the user's persisted layout (grid/list) so it always
  // resembles what's loading. Read once at mount.
  const skeletonView = useMemo(() => readPersistedSpaceView(), []);

  // Sequential transition phases:
  //  - "loading": only the skeleton (opacity 1), content not yet rendered;
  //  - "fadeOut": data arrived — skeleton fades to 0 while content stays hidden;
  //  - "fadeIn":  skeleton unmounted — content fades 0→1;
  //  - "done":    steady state.
  // The skeleton and content are never both visible.
  const [phase, setPhase] = useState<"loading" | "fadeOut" | "fadeIn" | "done">(
    "loading"
  );
  useEffect(() => {
    if (status !== "success") {
      setPhase("loading");
      return;
    }
    setPhase("fadeOut");
    const t1 = window.setTimeout(() => setPhase("fadeIn"), SKELETON_FADE_MS);
    const t2 = window.setTimeout(
      () => setPhase("done"),
      SKELETON_FADE_MS + CONTENT_FADE_MS
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [status]);

  const notFound = error?.response?.status === 404;
  const contentVisible = phase === "fadeIn" || phase === "done";
  const skeletonMounted = phase === "loading" || phase === "fadeOut";

  return (
    <ConditionalEnvironmentProvider
      condition={!!environmentRef.current}
      environment={environmentRef.current}
    >
      <TransferPageSkeleton
        childrenVerticalAlignment={status === "error" ? "center" : "top"}
        hasMailboxLoaded={!!environmentRef.current}
      >
        {status === "error" ? (
          <SpaceError notFound={notFound} />
        ) : (
          <Box sx={{ position: "relative" }}>
            {status === "success" && data && (
              <Box
                sx={{
                  opacity: contentVisible ? 1 : 0,
                  transition: `opacity ${CONTENT_FADE_MS}ms ease`,
                }}
              >
                <DesktopSpaceBrowser spaceUid={spaceUid} data={data} />
              </Box>
            )}
            {skeletonMounted && (
              <Box
                aria-hidden
                sx={{
                  // In flow while loading (gives the page height); overlaid on the
                  // (still-hidden) content while fading out, so there's no shift.
                  position: status === "success" ? "absolute" : "static",
                  inset: 0,
                  pointerEvents: "none",
                  opacity: phase === "fadeOut" ? 0 : 1,
                  transition: `opacity ${SKELETON_FADE_MS}ms ease`,
                }}
              >
                <DesktopSpaceSkeleton view={skeletonView} />
              </Box>
            )}
          </Box>
        )}
      </TransferPageSkeleton>
    </ConditionalEnvironmentProvider>
  );
}

// ==========================================================================
//                                   Error
// ==========================================================================

function SpaceError({ notFound }: { notFound: boolean }) {
  const { t } = useTranslation();

  // Non-404 (transient) errors keep a simple centered message.
  if (!notFound) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-8 text-center">
        <div className="text-[17px] font-bold text-[#15151b]">
          {t("Something went wrong")}
        </div>
        <div className="text-[13px] text-[#9a9aa4]">
          {t("The space could not be loaded. Please try again.")}
        </div>
      </div>
    );
  }

  // 404 "space unavailable" — a left-aligned explanation with a large, faint
  // bullhorn watermark on the right (design: publishedSpaceErrorPage). The
  // watermark sits behind the text and scales down / tucks off-edge on narrow
  // screens so the copy always stays readable.
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: 1080,
        mx: "auto",
        px: { xs: "24px", sm: "48px", md: "72px" },
        py: { xs: 6, md: 4 },
        display: "flex",
        alignItems: "center",
        minHeight: { md: 440 },
        overflow: "hidden",
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 560 }}>
        <Typography
          sx={{
            fontFamily: "IBM Plex Sans",
            fontSize: { xs: 24, sm: 28 },
            fontWeight: 700,
            lineHeight: 1.2,
            color: "#1a1a1a",
            mb: { xs: 3, sm: 4 },
          }}
        >
          {t("Public space unavailable")}
        </Typography>
        <Stack spacing={{ xs: 2.5, sm: 3 }}>
          {[
            t("This public space does not exist or is no longer available."),
            t(
              "Public access may have been withdrawn, or the space may have been deleted."
            ),
            t(
              "Please contact the person who provided you with the link to this space."
            ),
          ].map((line, i) => (
            <Typography
              key={i}
              sx={{
                fontFamily: "IBM Plex Sans",
                fontSize: { xs: 15, sm: 16 },
                lineHeight: 1.5,
                color: "#5c5c66",
              }}
            >
              {line}
            </Typography>
          ))}
        </Stack>
      </Box>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: "50%",
          // Centered behind the copy on phones; docked to the right on wider screens.
          left: { xs: "50%", sm: "auto" },
          right: { xs: "auto", sm: "0px", md: "24px" },
          transform: {
            xs: "translate(-50%, -50%)",
            sm: "translateY(-50%)",
          },
          width: { xs: 300, sm: 320, md: 397 },
          maxWidth: { xs: "85%", sm: "60%" },
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <BullhornWatermark />
      </Box>
    </Box>
  );
}

/**
 * Decorative bullhorn-with-question-mark watermark for the 404 page
 * (asset: Bullhorn.svg). Rendered inline so it inherits no external file and its
 * intrinsically light fills sit softly behind the error copy.
 */
function BullhornWatermark() {
  return (
    <svg viewBox="0 0 397 397" width="100%" height="100%" role="presentation">
      <g transform="translate(-587.305 -216.203)">
        <circle
          cx="198.5"
          cy="198.5"
          r="198.5"
          transform="translate(587.305 216.203)"
          fill="#f7f7f7"
          opacity="0.639"
        />
        <path
          d="M679.5,373.556l62.694-4.856,52.1-21.192,23.4-23.841,13.243,9.271V483.047l-13.243,5.3-30.907-26.931-49.889-18.543-3.972,22.516-3.975,31.788L702.9,499.824l-7.063-26.931V442.871L679.5,437.132l-8.387-40.176Z"
          transform="translate(1.618 2.573)"
          fill="#ededed"
          opacity="0.39"
        />
        <path
          d="M168.2,19.153c0-13.591-16.595-20.2-25.905-10.325l-8.973,9.461A102.076,102.076,0,0,1,64.839,50.014l-10.925.6H24.028A24.051,24.051,0,0,0,0,74.643v36.042a24.051,24.051,0,0,0,24.028,24.028v39.046a21.038,21.038,0,0,0,21.025,21.025H51.06a21.038,21.038,0,0,0,21.025-21.025V135.99a101.953,101.953,0,0,1,61.235,31.087l8.973,9.424c9.348,9.837,25.905,3.229,25.905-10.325V19.19ZM60.071,135.051v38.708a8.989,8.989,0,0,1-9.011,9.011H45.053a8.989,8.989,0,0,1-9.011-9.011V134.714H53.913ZM151,17.088a3,3,0,0,1,5.181,2.065V166.138A3,3,0,0,1,151,168.2l-8.936-9.424A114.352,114.352,0,0,0,84.1,125.891V59.4a114.082,114.082,0,0,0,57.968-32.889L151,17.088ZM72.085,61.465V123.9c-2.178-.263-4.355-.413-6.57-.563L54.4,122.7H24.066a12,12,0,0,1-12.014-12.014V74.643A12,12,0,0,1,24.066,62.629H54.4l11.151-.6c2.178-.113,4.393-.3,6.57-.563Z"
          transform="translate(668.305 315.108)"
          fill="#ededed"
        />
        <path
          d="M28.84,57.6a12.852,12.852,0,0,1,12.84-12.84H48.1A12.852,12.852,0,0,1,60.942,57.6v.722a12.813,12.813,0,0,1-5.9,10.794l-8.467,5.437A17.652,17.652,0,0,0,38.471,89.4v.281a6.42,6.42,0,0,0,12.84,0V89.4a4.835,4.835,0,0,1,2.207-4.053l8.467-5.437A25.712,25.712,0,0,0,73.782,58.3v-.722A25.679,25.679,0,0,0,48.1,31.9h-6.42A25.709,25.709,0,0,0,16,57.6a6.42,6.42,0,0,0,12.84,0Zm16.051,64.2a8.025,8.025,0,1,0-8.025-8.025A8.025,8.025,0,0,0,44.891,121.8Z"
          transform="translate(840.083 337.088)"
          fill="#ededed"
        />
      </g>
    </svg>
  );
}
